import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useLenis } from "lenis/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo, useMediaQuery } from "../../kit.jsx";
import { AAROH_ALBUM } from "../../data/album.js";
import { Btn, EASE_IN_OUT, Kicker, clamp01 } from "./shared.jsx";
import "./about.css";
import "./partnerships.css";

const MAIL = "mailto:founder@projectgrid.org?subject=Partnership%20proposal";
const album = (id) => AAROH_ALBUM.find((photo) => photo.id === id) ?? AAROH_ALBUM[0];
const albumShot = (id, caption) => {
  const photo = album(id);
  return { src: photo.src, small: photo.srcSmall, alt: photo.alt, caption };
};

const SLIDES = [
  {
    key: "tachyon",
    count: "01",
    name: "Tachyon India",
    programme: "AAROH",
    role: "Collaborator",
    logos: [["Tachyon India", "/images/partners/tachyon-india.png"]],
    line: "Ran AAROH with GRID. Tachyon brought experience running direct-entry hackathons for young developers, designers and creators.",
    href: "https://www.tachyonindia.org/",
    photo: albumShot("M087", "AAROH's opening screen names GRID, Tachyon India, CiPD and IIIT Delhi."),
  },
  {
    key: "cipd",
    count: "02",
    name: "CiPD, IIIT Delhi",
    programme: "AAROH",
    role: "Institutional collaborator",
    logos: [["CiPD, IIIT Delhi", "/images/partners/cipd-iiitd.png"]],
    line: "The Centre for Intelligent Product Development at IIIT Delhi. It works on moving research toward production-ready hardware.",
    href: "https://cipd.iiitd.ac.in/",
    photo: albumShot("M609", "A winning team with its trophy. The screen behind names the collaborators."),
  },
  {
    key: "gradcircle",
    count: "03",
    name: "GradCircle",
    programme: "AAROH",
    role: "Supporter",
    logos: [["GradCircle", "/images/partners/supporters/gradcircle.webp"]],
    line: "Supported AAROH at IIIT Delhi.",
    photo: albumShot("M390", "A session at AAROH beside the GradCircle banner."),
  },
  {
    key: "supporters",
    count: "04–08",
    name: "Five more supporters",
    programme: "AAROH",
    role: "Supporters",
    logos: [
      ["Stacks KB", "/images/partners/supporters/stacks.png"],
      ["Fotocart", "/images/partners/supporters/fotocart.png"],
      [".xyz", "/images/partners/supporters/xyz.png"],
      ["GitHub", "/images/partners/supporters/github.png"],
      ["Skills4U", "/images/partners/supporters/skills4u.webp"],
    ],
    line: "Stacks KB, Fotocart, .xyz, GitHub and Skills4U supported AAROH at IIIT Delhi.",
    photo: albumShot("M083", "The AAROH partners banner next to the title screen."),
  },
  {
    key: "hp",
    count: "09",
    name: "HP",
    programme: "Aurora",
    role: "Prize laptops",
    logos: [["HP", "/images/partners/supporters/hp.png"]],
    line: "Gave the prize laptops for Aurora, GRID's online hackathon. Overall winner Jerovin Floyd received one.",
    photo: {
      src: "/images/grid-photo-10-p8.jpg",
      alt: "Aurora overall winner Jerovin Floyd Vincent Joseph holding his HP prize laptop",
      caption: "Jerovin Floyd with his HP prize laptop.",
    },
  },
  {
    key: "toh",
    count: "10",
    name: "Tale of Humankind Foundation",
    programme: "Metamorphosis",
    role: "Organiser",
    logos: [["Tale of Humankind Foundation", "/images/partners/tale-of-humankind.webp"]],
    line: "Led Metamorphosis with IIT Delhi's outreach office. It also runs the Young Changemakers Bootcamp, where GRID began. GRID was the outreach partner.",
    href: "https://taleofhumankind.org/",
    photo: {
      src: "/images/metamorphosis/album/meta-745-cohort-group-photo-1600.webp",
      alt: "Metamorphosis students packing a lecture hall for a group photo at IIT Delhi",
      caption: "The Metamorphosis cohort at IIT Delhi.",
    },
  },
  {
    key: "aoni",
    count: "11",
    name: "IIT Delhi Office of Academic Outreach",
    programme: "Metamorphosis",
    role: "Organiser",
    logos: [["IIT Delhi, Office of Academic Outreach & New Initiatives", "/images/partners/iit-delhi-aoni.png"]],
    line: "Organised Metamorphosis with Tale of Humankind Foundation. GRID was the outreach partner and helped bring students in.",
    href: "https://academicoutreach.iitd.ac.in/",
    photo: {
      src: "/images/metamorphosis/album/meta-679-lathe-demo-in-workshop-1600.webp",
      alt: "An instructor explains a lathe part to Metamorphosis students during a machine shop visit at IIT Delhi",
      caption: "A machine shop visit during Metamorphosis.",
    },
  },
];

const ORGS = SLIDES.flatMap((slide, index) => slide.logos.map(([name, logo]) => ({ name, logo, index })));
const HERO_PHOTO = album("M400");

/* ---------- Logo tile ---------- */

function LogoTile({ logos, size = "large" }) {
  return (
    <span className={`ab-ptile ab-ptile--${size} ${logos.length > 1 ? "is-many" : ""}`}>
      {logos.map(([name, logo]) => (
        <img key={name} src={logo} alt={`${name} logo`} loading="lazy" decoding="async" />
      ))}
    </span>
  );
}

/* ---------- Hero ---------- */

function PartnersHero({ onJump }) {
  const reduceMotion = useReducedMotion();
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 18 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });
  return (
    <section className="ab-phero3" aria-labelledby="ab-phero-title">
      <div className="ab-phero3__copy">
        <motion.div {...appear(0)}><Kicker>Partners</Kicker></motion.div>
        <MaskWords as="h1" id="ab-phero-title" className="ab-display ab-display--mid" text="Eleven organisations. What each one did." onMount delay={0.08} />
        <motion.p className="ab-lede" {...appear(0.4)}>
          Across AAROH, Aurora and Metamorphosis. Each one is credited under its programme, with its role named. A meeting is not an endorsement or a partnership.
        </motion.p>
        <motion.div className="gh-actions" {...appear(0.5)}>
          <Btn href={MAIL}>Propose a collaboration</Btn>
          <Btn href="#ab-reel" variant="ghost">Meet them</Btn>
        </motion.div>
      </div>
      <motion.figure
        className="ab-phero3__photo"
        initial={reduceMotion ? false : { clipPath: "inset(100% 0% 0% 0% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.3, delay: 0.2, ease: EASE_IN_OUT }}
      >
        <motion.img
          src={HERO_PHOTO.src}
          alt={HERO_PHOTO.alt}
          width={HERO_PHOTO.width}
          height={HERO_PHOTO.height}
          fetchPriority="high"
          initial={reduceMotion ? false : { scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, delay: 0.2, ease: EASE_OUT }}
        />
        <figcaption>The partners banner at AAROH, IIIT Delhi.</figcaption>
      </motion.figure>
      <ul className="ab-phero3__logos" aria-label="Jump to a partner">
        {ORGS.map((org, i) => (
          <motion.li
            key={org.name}
            initial={reduceMotion ? false : { opacity: 0, y: 26, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.55 + i * 0.05, ease: EASE_OUT }}
          >
            <button type="button" onClick={() => onJump(org.index)} aria-label={`Jump to ${org.name}`}>
              <img src={org.logo} alt="" loading="eager" decoding="async" />
            </button>
          </motion.li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Pinned reel ---------- */

function SlideText({ slide }) {
  return (
    <div className="ab-reel__text">
      <div className="ab-reel__meta">
        <span className="ab-reel__count"><strong>{slide.count}</strong> / 11</span>
        <span className="ab-reel__pill">{slide.programme} · {slide.role}</span>
      </div>
      <h3>{slide.name}</h3>
      <p>{slide.line}</p>
      {slide.href ? (
        <a className="gh-link ab-reel__visit" href={slide.href} target="_blank" rel="noreferrer">
          Visit {slide.name.split(",")[0]} <ArrowUpRight weight="bold" aria-hidden="true" />
        </a>
      ) : null}
    </div>
  );
}

function PinnedReel({ jumpRef }) {
  const ref = useRef(null);
  const lenis = useLenis();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const n = SLIDES.length;

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = Math.min(n - 1, Math.max(0, Math.floor(clamp01(value) * n)));
    setActive((previous) => (previous === next ? previous : next));
  });

  const slideFill = useTransform(scrollYProgress, (value) => {
    const local = clamp01(value) * n - active;
    return clamp01(local);
  });

  jumpRef.current = (index) => {
    const section = ref.current;
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.scrollY;
    const span = section.offsetHeight - window.innerHeight;
    const target = top + (span * (index + 0.5)) / n;
    if (lenis) lenis.scrollTo(target, { duration: 1.4 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };

  const slide = SLIDES[active];
  return (
    <section id="ab-reel" className="ab-reel" ref={ref} style={{ "--slides": n }} aria-label="Partners, one at a time">
      <div className="ab-reel__stage">
        <div className="ab-reel__left">
          <div className="ab-reel__tilewrap">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={slide.key}
                className="ab-reel__tile"
                initial={{ y: 80, opacity: 0, rotate: -4 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: -80, opacity: 0, rotate: 3 }}
                transition={{ duration: 0.7, ease: EASE_OUT }}
              >
                <LogoTile logos={slide.logos} />
              </motion.div>
            </AnimatePresence>
          </div>
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={slide.key}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.45, ease: EASE_OUT }}
            >
              <SlideText slide={slide} />
            </motion.div>
          </AnimatePresence>
        </div>
        <figure className="ab-reel__photo">
          <AnimatePresence initial={false}>
            <motion.div
              key={slide.key}
              className="ab-reel__frame"
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              exit={{ opacity: 1 }}
              transition={{ duration: 0.9, ease: EASE_IN_OUT }}
            >
              <motion.img
                src={slide.photo.src}
                alt={slide.photo.alt}
                initial={{ scale: 1.16 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.4, ease: EASE_OUT }}
              />
            </motion.div>
          </AnimatePresence>
          <figcaption key={slide.key}>{slide.photo.caption}</figcaption>
        </figure>
        <ol className="ab-reel__rail" aria-label="Partners">
          {SLIDES.map((item, index) => (
            <li key={item.key} className={index === active ? "is-active" : index < active ? "is-done" : undefined}>
              <button type="button" onClick={() => jumpRef.current(index)}>
                <span className="ab-reel__bar">
                  <motion.span style={index === active ? { scaleX: slideFill } : undefined} />
                </span>
                <span className="ab-reel__railname">{item.name}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function StackedReel() {
  const reduceMotion = useReducedMotion();
  return (
    <section id="ab-reel" className="ab-reel-list" aria-label="Partners">
      {SLIDES.map((slide) => (
        <motion.article
          key={slide.key}
          className="ab-reel-card"
          initial={reduceMotion ? false : { opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          <figure className="ab-reel-card__photo">
            <img src={slide.photo.small ?? slide.photo.src} alt={slide.photo.alt} loading="lazy" decoding="async" />
            <span className="ab-reel-card__tile"><LogoTile logos={slide.logos} size="small" /></span>
          </figure>
          <SlideText slide={slide} />
          <p className="ab-reel-card__caption">{slide.photo.caption}</p>
        </motion.article>
      ))}
    </section>
  );
}

/* ---------- How a collaboration works ---------- */

const STEPS = [
  ["Start with something specific", "Mentoring in an area you know well, equipment or a venue, programme resources, or a problem students can investigate."],
  ["Tell us the details", "Who you represent, what you can offer, when you are free and any conditions. Say if it is one event or a longer collaboration."],
  ["Agree the scope first", "Responsibilities, participant safeguards, costs, credits and any use of names or logos are agreed before anything is announced."],
  ["Credit the exact role", "The programme page names what you did. Your logo sits under that programme only."],
];

function StepItem({ step, index, progress, reduceMotion }) {
  const at = (index + 0.4) / STEPS.length;
  const opacity = useTransform(progress, (v) => 0.3 + 0.7 * clamp01((v - at + 0.12) / 0.12));
  const scale = useTransform(progress, (v) => clamp01((v - at + 0.04) / 0.06));
  return (
    <motion.li style={reduceMotion ? undefined : { opacity }}>
      <span className="ab-flow__node" aria-hidden="true">
        <motion.span style={reduceMotion ? undefined : { scale }} />
      </span>
      <span className="ab-flow__num">{String(index + 1).padStart(2, "0")}</span>
      <strong>{step[0]}</strong>
      <p>{step[1]}</p>
    </motion.li>
  );
}

function CollaborationFlow() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.55"] });
  const fill = useTransform(scrollYProgress, (v) => clamp01(v));
  const vertical = useMediaQuery("(max-width: 899px)");
  return (
    <section className="ab-flow" aria-labelledby="ab-flow-title">
      <header className="ab-flow__head">
        <MaskWords id="ab-flow-title" className="ab-h2" text="How a collaboration works." />
        <p className="ab-body">Four steps, in this order. Nothing is announced until the scope and the credit are agreed.</p>
      </header>
      <div className="ab-flow__stage" ref={ref}>
        <span className="ab-flow__track" aria-hidden="true">
          <motion.span style={reduceMotion ? undefined : vertical ? { scaleY: fill } : { scaleX: fill }} />
        </span>
        <ol>
          {STEPS.map((step, index) => (
            <StepItem key={step[0]} step={step} index={index} progress={scrollYProgress} reduceMotion={reduceMotion} />
          ))}
        </ol>
      </div>
    </section>
  );
}

export default function PartnershipsPage() {
  const reduceMotion = useReducedMotion();
  const compact = useMediaQuery("(max-width: 899px)");
  const pinned = !compact && !reduceMotion;
  const jumpRef = useRef(() => {});
  const jump = (index) => {
    if (pinned) {
      jumpRef.current(index);
      return;
    }
    document.querySelectorAll(".ab-reel-card")[index]?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  return (
    <PageFrame className="rd-page ab-page ab-page--partners">
      <Seo
        title="Project GRID Partnerships"
        path="/partnerships"
        description="The organisations behind Project GRID's programmes, and the exact role each one played."
      />
      <main>
        <PartnersHero onJump={jump} />
        {pinned ? <PinnedReel jumpRef={jumpRef} /> : <StackedReel />}
        <CollaborationFlow />
      </main>
    </PageFrame>
  );
}
